import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Alert, Btn, FormInput, FormSelect } from '../components/UI';
import api from '../api/client';

const COUNTRIES = [
  'United States','United Kingdom','Canada','Australia',
  'Germany','France','Netherlands','Sweden','Ireland','New Zealand','Other',
].map(c => ({ value: c, label: c }));

export default function Settings() {
  const { user } = useAuth();

  const [profile, setProfile] = useState({
    name:    user?.name    || '',
    country: user?.country || 'United States',
  });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [profileMsg,  setProfileMsg]  = useState({ type: '', text: '' });
  const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });
  const [savingProfile,  setSavingProfile]  = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [notifs, setNotifs] = useState({
    earnings_alerts:    true,
    withdrawal_updates: true,
    weekly_summary:     false,
    platform_activity:  true,
  });

  async function saveProfile(e) {
    e.preventDefault();
    setProfileMsg({ type: '', text: '' });
    if (!profile.name.trim() || profile.name.length < 2) {
      setProfileMsg({ type: 'error', text: 'Name must be at least 2 characters.' }); return;
    }
    setSavingProfile(true);
    try {
      await api.patch('/auth/me', { name: profile.name, country: profile.country });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile.' });
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(e) {
    e.preventDefault();
    setPasswordMsg({ type: '', text: '' });
    if (!passwords.current)                       { setPasswordMsg({ type:'error', text:'Current password required.' }); return; }
    if (passwords.newPass.length < 8)             { setPasswordMsg({ type:'error', text:'New password must be at least 8 characters.' }); return; }
    if (!/[A-Z]/.test(passwords.newPass))         { setPasswordMsg({ type:'error', text:'New password must contain an uppercase letter.' }); return; }
    if (passwords.newPass !== passwords.confirm)  { setPasswordMsg({ type:'error', text:'Passwords do not match.' }); return; }
    setSavingPassword(true);
    try {
      await api.patch('/auth/me/password', { current_password: passwords.current, new_password: passwords.newPass });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password.' });
    } finally {
      setSavingPassword(false);
    }
  }

  function toggleNotif(key) {
    setNotifs(n => ({ ...n, [key]: !n[key] }));
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h1 style={styles.h1}>Account Settings</h1>
        <p style={styles.sub}>Manage your profile, security, and notification preferences.</p>
      </div>

      {/* Profile */}
      <Card title="Profile Information" style={{ marginBottom: 24 }}>
        {profileMsg.text && <Alert type={profileMsg.type} message={profileMsg.text} />}
        <form onSubmit={saveProfile}>
          <div style={styles.formGrid} className="form-grid-2">
            <FormInput label="Full Name" value={profile.name}
              onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} required />
            <FormInput label="Email Address" type="email" value={user?.email || ''} disabled
              style={{ background: '#f8f9fc', color: '#9099b4' }} />
          </div>
          <FormSelect label="Country" options={COUNTRIES}
            value={profile.country} onChange={e => setProfile(p => ({ ...p, country: e.target.value }))} />
          <div style={styles.fieldRow}>
            <div style={styles.fieldInfo}>
              <strong>Member since</strong>
              <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month:'long', year:'numeric' }) : '—'}</span>
            </div>
            <div style={styles.fieldInfo}>
              <strong>Account role</strong>
              <span style={{ background:'#eef1ff', color:'#4f6ef7', padding:'2px 10px', borderRadius:20, fontSize:12, fontWeight:700, textTransform:'capitalize' }}>{user?.role}</span>
            </div>
          </div>
          <Btn loading={savingProfile} type="submit" style={{ marginTop: 8 }}>Save Changes</Btn>
        </form>
      </Card>

      {/* Password */}
      <Card title="Change Password" style={{ marginBottom: 24 }}>
        {passwordMsg.text && <Alert type={passwordMsg.type} message={passwordMsg.text} />}
        <form onSubmit={savePassword}>
          <FormInput label="Current Password" type="password" placeholder="Your current password"
            value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} />
          <div style={styles.formGrid} className="form-grid-2">
            <FormInput label="New Password" type="password" placeholder="Min 8 chars, 1 uppercase, 1 number"
              value={passwords.newPass} onChange={e => setPasswords(p => ({ ...p, newPass: e.target.value }))} />
            <FormInput label="Confirm New Password" type="password" placeholder="Repeat new password"
              value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} />
          </div>
          <Btn loading={savingPassword} type="submit">Update Password</Btn>
        </form>
      </Card>

      {/* Notifications */}
      <Card title="Notification Preferences" style={{ marginBottom: 24 }}>
        {[
          ['earnings_alerts',    'Earnings alerts',        'Get notified when earnings are credited to your account'],
          ['withdrawal_updates', 'Withdrawal updates',     'Status changes on your withdrawal requests'],
          ['weekly_summary',     'Weekly summary email',   'A weekly report of your earnings and platform activity'],
          ['platform_activity',  'Platform activity',      'When our team completes tasks on your platform accounts'],
        ].map(([key, label, desc]) => (
          <div key={key} style={styles.notifRow}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1f5e' }}>{label}</div>
              <div style={{ fontSize: 13, color: '#9099b4' }}>{desc}</div>
            </div>
            <div onClick={() => toggleNotif(key)} style={{
              width: 44, height: 24, borderRadius: 12,
              background: notifs[key] ? '#4f6ef7' : '#e2e6f0',
              position: 'relative', cursor: 'pointer', flexShrink: 0,
              transition: 'background .2s',
            }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: notifs[key] ? 23 : 3,
                transition: 'left .2s',
                boxShadow: '0 1px 3px rgba(0,0,0,.2)',
              }} />
            </div>
          </div>
        ))}
      </Card>

      {/* Security */}
      <Card title="Security">
        {[
          { label: 'Two-Factor Authentication', desc: 'Add an extra layer of security to your account', action: <Btn variant="outline" style={{ fontSize: 12, padding: '7px 14px' }}>Enable 2FA</Btn> },
          { label: 'Active Sessions',           desc: '1 active session on this device',                action: <Btn variant="ghost"   style={{ fontSize: 12, padding: '7px 14px', color: '#dc2626' }}>Revoke all</Btn> },
          { label: 'Download My Data',          desc: 'Export a copy of your earnings and activity',    action: <Btn variant="outline" style={{ fontSize: 12, padding: '7px 14px' }}>Export</Btn> },
        ].map(({ label, desc, action }) => (
          <div key={label} style={styles.securityRow}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1f5e' }}>{label}</div>
              <div style={{ fontSize: 13, color: '#9099b4' }}>{desc}</div>
            </div>
            {action}
          </div>
        ))}
      </Card>
    </div>
  );
}

const styles = {
  wrap:        { padding: '32px', fontFamily: "'DM Sans',sans-serif" },
  header:      { marginBottom: 28 },
  h1:          { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', margin: 0 },
  sub:         { fontSize: 14, color: '#9099b4', marginTop: 4 },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  fieldRow:    { display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' },
  fieldInfo:   { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 },
  notifRow:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #f0f2f8', gap: 16 },
  securityRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid #f0f2f8', gap: 16 },
};
